import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Container, Row, Col } from "reactstrap";
import MainNavigation from "../components/MainNavigation";
import HeroSectionCard from "../components/HeroSectionCard";
import ProductInfoSection from "../components/ProductDetails/ProductInfoSection";
import { getProduct } from "../store/actions/GlobalAction";
import {
  addCartDetails,
  addToCartAction,
  clearCartAction,
  clearCartDetails,
  getAddOns,
} from "../store/actions/cartAction";
import { getRestaurantsAction } from "../store/actions/restaurantsAction";
import HolidayNotice from "../components/RestaurantMenu/HolidayNotice";
import RestaurantLoader from "../utils/RestaurantLoader";
import { calculateTotalWithCharges } from "../utils/calculateTotalWithCharges";

const initialSelectedItemState = {
  id: null,
  name: "",
  images: [],
  restaurantID: "",
  currency: "",
  size: null,
  price: 0,
  addons: [],
  notes: "",
  quantity: 1,
  totalPrice: 0,
};

const ProductDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(initialSelectedItemState);
  const { darkMode, singleProduct } = useSelector((state) => state.global);
  const { restaurantsDetail } = useSelector((state) => state.restaurants);
  const { uid } = useSelector((state) => state.authUser);
  const { cartItems, addOnsDetail, cartDetails } = useSelector(
    (state) => state.cart
  );
  const [loader, setLoader] = useState(false);
  const recommendedProducts = location?.state?.recommendedProducts || [];
  const addonLabels = location?.state?.addOns || [];
  const [isHoliday, setIsHoliday] = useState({
    isHoliday: false,
    title: "",
    storeTiming: false,
    type: "",
  });

  // const existingCartItem = cartItems?.find((item) => item?.id === id);

  const addToCart = (e) => {
    e.preventDefault();

    if (isHoliday?.isHoliday || isHoliday?.storeTiming) {
      const typeLabel =
        isHoliday?.type === "dinein"
          ? "Dine-in"
          : isHoliday?.type === "delivery"
          ? "Delivery"
          : "Takeaway";
      const typeIcon =
        isHoliday?.type === "dinein"
          ? "🍽️"
          : isHoliday?.type === "delivery"
          ? "🚚"
          : "🛍️";

      toast.warning(
        isHoliday?.isHoliday
          ? `📅 Attention: The ${restaurantsDetail?.name} store is closed today due to ${isHoliday?.title}. Please check back tomorrow.`
          : `${typeIcon} Notice: ${typeLabel} service is currently unavailable as operating hours have ended. Please select a different option or check back tomorrow.`,
        {
          position: "top-center",
          autoClose: 5000,
          style: {
            backgroundColor: "#fff3cd",
            color: "#856404",
            fontWeight: "500",
            fontSize: "15px",
          },
        }
      );

      if (isHoliday?.storeTiming) {
        history.push("/order-options");
        dispatch(clearCartAction());
      } else if (isHoliday?.isHoliday) {
        history.push("/restaurants");
        dispatch(clearCartAction());
        dispatch(clearCartDetails());
      }
      return;
    }

    if (!selectedItem.size) {
      toast.warning("Please select a size");
      return;
    }

    setLoader(true);

    try {
      const { finalTotal } = calculateTotalWithCharges(
        cartItems,
        selectedItem,
        restaurantsDetail?.additionalCharges || []
      );

      if (finalTotal > 10000) {
        toast.warning(
          `🚫 Current order total after adding this item: ${finalTotal.toFixed(
            2
          )} ${
            restaurantsDetail?.currency || "$"
          }.\n\n❌ Order limit exceeded! You cannot add this item because the maximum allowed order value is 10,000 ${
            restaurantsDetail?.currency || "$"
          }. Please remove some items from your cart to continue.`
        );
        setLoader(false);
        history.goBack();
        return;
      }

      if (!cartDetails?.restaurantId) {
        dispatch(addCartDetails({ restaurantId: selectedItem?.restaurantID }));
      }

      if (
        cartDetails?.restaurantId &&
        selectedItem?.restaurantID &&
        cartDetails.restaurantId !== selectedItem.restaurantID
      ) {
        const existingCart = cartDetails || {};
        dispatch(getRestaurantsAction(selectedItem.restaurantID));
        dispatch(
          addCartDetails({
            ...existingCart,
            restaurantId: selectedItem.restaurantID,
          })
        );
      }

      const cartItem = {
        ...selectedItem,
        totalPrice: selectedItem?.totalPrice?.toFixed(2),
      };

      dispatch(
        addToCartAction(cartItem, () => {
          history.goBack();
          setTimeout(() => {
            setSelectedItem(initialSelectedItemState);
          }, 500);
        })
      );
    } finally {
      setTimeout(() => {
        setLoader(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (singleProduct) {
      setSelectedItem((prev) => ({
        ...prev,
        id: singleProduct.id,
        name: singleProduct.name,
        images: singleProduct.images || [],
        restaurantID: singleProduct.restaurantID,
        currency: singleProduct.currency || "",
        estimatedTime: singleProduct?.estimatedTime || "",
        size: singleProduct.variants?.[0]?.size || null,
        price: singleProduct.variants?.[0]?.price || 0,
        totalPrice: singleProduct.variants?.[0]?.price || 0,
      }));
    }
  }, [singleProduct]);

  useEffect(() => {
    dispatch(getAddOns(addonLabels));
  }, [addonLabels, dispatch]);

  useEffect(() => {
    const restaurantID = cartDetails?.restaurantId;
    if (restaurantID) {
      dispatch(getRestaurantsAction(restaurantID));
    }
  }, [cartDetails?.restaurantId, dispatch]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getProduct(id, () => setIsLoading(false)));
  }, [dispatch, id]);

  if (isLoading) {
    return (
      <Container className="vh-100 d-flex justify-content-center align-items-center">
        <RestaurantLoader message="Fetching Product Details" />
      </Container>
    );
  }

  return (
    <Container className="product_details">
      <HolidayNotice
        restaurant={restaurantsDetail}
        setIsHoliday={setIsHoliday}
      />
      <MainNavigation title={singleProduct?.name} />
      <Row>
        <Col lg={5} md={5}>
          <HeroSectionCard
            slideImages={singleProduct?.images}
            menuID={singleProduct?.id}
            customerID={uid}
            title={singleProduct?.name}
            subTitle={
              singleProduct?.currency && singleProduct?.variants?.[0]?.price
                ? `${singleProduct?.currency} ${singleProduct?.variants?.[0].price}`
                : "Price not available"
            }
            content={singleProduct?.comments}
            label={singleProduct?.labels}
          />
        </Col>
        <Col lg={7} md={7} className="px-1 mt-4">
          <ProductInfoSection
            id={id}
            isLoading={isLoading}
            singleProduct={singleProduct}
            // existingCartItem={existingCartItem}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            addonLabels={addOnsDetail}
            darkMode={darkMode}
            addToCart={addToCart}
            loading={loader}
            recommendedProducts={recommendedProducts}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ProductDetails;
